import {
    PayloadAction,
    createAsyncThunk,
    createEntityAdapter,
    createSlice,
} from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { getAxiosErrorMessage } from '../../service/axios/axios.call.service';
import { PaginatedResponse } from '../../service/model/paginated-response';
import { getDefaultStaffAttendanceSearchCriteria, StaffAttendance, StaffAttendanceRequest, StaffAttendanceSearchCriteria } from '../../service/model/staff-attendance.model';
import * as staffAttendanceService from '../../service/staff-attendance.service';
import { NotificationType } from '../../shared/enums';
import { showNotification } from '../alerts/alert.slice';

export interface StaffAttendanceState {
    isLoading: boolean;
    staffAttendances: StaffAttendance[];
    summary?: any;
    page?: PaginatedResponse<StaffAttendance>;
    totalPages: number;
    currentPage: number;
    totalRecords: number;
}

const staffAttendanceAdapter = createEntityAdapter({
    selectId: (staffAttendance: StaffAttendance) => staffAttendance.id,
    sortComparer: (a, b) => (b.checkIn || '').localeCompare(a.checkIn || ''),
});

export const initialState = staffAttendanceAdapter.getInitialState({
    isLoading: false,
    staffAttendances: [],
    totalPages: 0,
    currentPage: 0,
    totalRecords: 0,
} as StaffAttendanceState);

export const fetchStaffAttendanceSummary = createAsyncThunk<any>(
    'staffAttendance/summary',
    async (_, { rejectWithValue }) => {
        return staffAttendanceService
            .getStaffAttendanceSummary()
            .catch((errors) => {
                return rejectWithValue(errors);
            });
    },
);

export const fetchStaffAttendancesPage = createAsyncThunk<
    PaginatedResponse<StaffAttendance>,
    StaffAttendanceSearchCriteria
>('staffAttendance/page', async (criteria, { rejectWithValue }) => {
    return staffAttendanceService
        .getStaffAttendancePage(criteria)
        .catch((errors) => {
            return rejectWithValue(errors);
        });
});

export const fetchMyAttendancePage = createAsyncThunk<
    PaginatedResponse<StaffAttendance>,
    StaffAttendanceSearchCriteria
>('staffAttendance/me', async (criteria, { rejectWithValue }) => {
    return staffAttendanceService
        .getMyAttendance(criteria)
        .catch((errors) => {
            return rejectWithValue(errors);
        });
});

export const createStaffAttendance = createAsyncThunk<
    StaffAttendance,
    StaffAttendanceRequest
>('staffAttendance/create', async (request, { rejectWithValue, dispatch }) => {
    return staffAttendanceService
        .createStaffAttendance(request)
        .then((response) => {
            dispatch(
                showNotification({
                    message: 'Staff attendance created successfully',
                    type: NotificationType.Success,
                }),
            );
            dispatch(
                fetchStaffAttendancesPage(getDefaultStaffAttendanceSearchCriteria()),
            );
            dispatch(fetchStaffAttendanceSummary());
            return response;
        })
        .catch((errors) => {
            dispatch(
                showNotification({
                    message: getAxiosErrorMessage(errors.response),
                    type: NotificationType.Error,
                }),
            );
            return rejectWithValue(errors);
        });
});

export const updateStaffAttendance = createAsyncThunk<
    StaffAttendance,
    StaffAttendanceRequest
>('staffAttendance/update', async (request, { rejectWithValue, dispatch }) => {
    return staffAttendanceService
        .updateStaffAttendance(request)
        .then((response) => {
            dispatch(
                showNotification({
                    message: 'Staff attendance updated successfully',
                    type: NotificationType.Success,
                }),
            );
            dispatch(fetchStaffAttendanceSummary());
            return response;
        })
        .catch((errors) => {
            dispatch(
                showNotification({
                    message: getAxiosErrorMessage(errors.response),
                    type: NotificationType.Error,
                }),
            );
            return rejectWithValue(errors);
        });
});

export const deleteStaffAttendance = createAsyncThunk<number, number>(
    'staffAttendance/delete',
    async (request, { rejectWithValue, dispatch }) => {
        return staffAttendanceService
            .deleteStaffAttendance(request)
            .then((response) => {
                dispatch(
                    showNotification({
                        message: 'Staff attendance deleted successfully',
                        type: NotificationType.Success,
                    }),
                );
                dispatch(fetchStaffAttendanceSummary());
                return response;
            })
            .catch((errors) => {
                dispatch(
                    showNotification({
                        message: getAxiosErrorMessage(errors.response),
                        type: NotificationType.Error,
                    }),
                );
                return rejectWithValue(errors);
            });
    },
);

const staffAttendanceSlice = createSlice({
    name: 'staffAttendance',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchStaffAttendanceSummary.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                fetchStaffAttendanceSummary.fulfilled,
                (state, action: PayloadAction<any>) => {
                    state.summary = action.payload;
                    state.isLoading = false;
                },
            )
            .addCase(fetchStaffAttendanceSummary.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(fetchStaffAttendancesPage.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                fetchStaffAttendancesPage.fulfilled,
                (
                    state,
                    action: PayloadAction<PaginatedResponse<StaffAttendance>>,
                ) => {
                    staffAttendanceAdapter.setAll(state, action.payload.data);
                    state.currentPage = action.payload.currentPage;
                    state.totalPages = action.payload.totalPages;
                    state.totalRecords = action.payload.totalRecords;
                    state.isLoading = false;
                },
            )
            .addCase(fetchStaffAttendancesPage.rejected, (state) => {
                state.isLoading = false;
            })
            
            .addCase(fetchMyAttendancePage.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                fetchMyAttendancePage.fulfilled,
                (
                    state,
                    action: PayloadAction<PaginatedResponse<StaffAttendance>>,
                ) => {
                    staffAttendanceAdapter.setAll(state, action.payload.data);
                    state.currentPage = action.payload.currentPage;
                    state.totalPages = action.payload.totalPages;
                    state.totalRecords = action.payload.totalRecords;
                    state.isLoading = false;
                },
            )
            .addCase(fetchMyAttendancePage.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(createStaffAttendance.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                createStaffAttendance.fulfilled,
                (state, action: PayloadAction<StaffAttendance>) => {
                    staffAttendanceAdapter.addOne(state, action.payload);
                    state.isLoading = false;
                },
            )
            .addCase(createStaffAttendance.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(updateStaffAttendance.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                updateStaffAttendance.fulfilled,
                (state, action: PayloadAction<StaffAttendance>) => {
                    staffAttendanceAdapter.upsertOne(state, action.payload);
                    state.isLoading = false;
                },
            )
            .addCase(updateStaffAttendance.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(deleteStaffAttendance.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                deleteStaffAttendance.fulfilled,
                (state, action: PayloadAction<number>) => {
                    staffAttendanceAdapter.removeOne(state, action.payload);
                    state.isLoading = false;
                },
            )
            .addCase(deleteStaffAttendance.rejected, (state) => {
                state.isLoading = false;
            });
    },
});

export const {
    selectAll: selectStaffAttendances,
    selectById: selectStaffAttendanceById,
} = staffAttendanceAdapter.getSelectors<RootState>(
    (state) => state.staffAttendance,
);

export const selectIsLoading = (state: RootState) =>
    state.staffAttendance.isLoading;
export const selectStaffAttendanceSummary = (state: RootState) =>
    state.staffAttendance.summary;
export const selectCurrentPage = (state: RootState) =>
    state.staffAttendance.currentPage;
export const selectTotalPages = (state: RootState) =>
    state.staffAttendance.totalPages;
export const selectTotalRecords = (state: RootState) =>
    state.staffAttendance.totalRecords;

export default staffAttendanceSlice.reducer;
